import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  Label,
  WithToolTip,
} from "@atlas/core";
import { formatIsoDateTimeDisplay, useTranslation } from "@atlas/sdk";
import type { IHubPackageDeployment } from "@models";
import { resolveHubPackageDeploymentStatusBadge } from "@utils";
import {
  Building,
  Building2,
  CalendarClock,
  Package,
  Type,
  User,
} from "lucide-react";
import { useMemo } from "react";

interface IDeploymentSummaryCardProps {
  isPopover: boolean;
  deployment: IHubPackageDeployment | null;
  itemCount: number;
  packageDescription?: string;
  isLoading?: boolean;
}

export function DeploymentSummaryCard({
  isPopover,
  deployment,
  isLoading,
}: IDeploymentSummaryCardProps) {
  const { t } = useTranslation();

  const formattedInstalledDate = useMemo(() => {
    if (!deployment?.installedDatetime) return null;
    const d = new Date(deployment.installedDatetime);
    if (Number.isNaN(d.getTime())) return null;
    return formatIsoDateTimeDisplay(deployment.installedDatetime);
  }, [deployment?.installedDatetime]);

  const solutionLabel = useMemo(() => {
    const solutionConfig = deployment?.config?.find((c) => c.solution);
    return solutionConfig?.solution?.label || null;
  }, [deployment?.config]);

  return (
    <Card
      isLoading={isLoading}
      className={cn(isPopover && "border-0 shadow-none")}>
      {!isPopover && (
        <CardHeader>
          <CardTitle className="flex w-full items-center justify-between gap-2 border-0">
            <div className="flex items-center gap-2 overflow-hidden h-full">
              <Label className="truncate text-lg">
                {solutionLabel ||
                  t("hub.checkout.deployment_summary", {
                    defaultValue: "Deployment Summary",
                  })}
              </Label>
              {deployment?.status &&
                (() => {
                  const badge = resolveHubPackageDeploymentStatusBadge(
                    deployment.status,
                  );
                  return (
                    <Badge
                      variant={badge.variant}
                      className="capitalize flex items-center gap-1">
                      <badge.icon className="size-3" />
                      {badge.label}
                    </Badge>
                  );
                })()}
            </div>
          </CardTitle>
        </CardHeader>
      )}
      <CardContent className={cn("flex flex-col gap-4", isPopover && "p-0")}>
        {deployment?.name && (
          <div className="grid grid-cols-[auto_1fr_2fr] items-center gap-2 text-sm text-muted-foreground">
            <Type className="size-4 shrink-0" />
            <Label className="font-medium truncate min-w-0">
              {t("hub.checkout.deployment_name", {
                defaultValue: "Name",
              })}
              :
            </Label>
            <Label className="truncate min-w-0" title={deployment.name}>
              {deployment.name}
            </Label>
          </div>
        )}

        {deployment?.targetSolutionName && (
          <div className="grid grid-cols-[auto_1fr_2fr] items-center gap-2 text-sm text-muted-foreground">
            <Package className="size-4 shrink-0" />
            <Label className="font-medium truncate min-w-0">
              {t("hub.catalog_subscribe.solution_name", {
                defaultValue: "Solution Name",
              })}
              :
            </Label>
            <WithToolTip
              tooltipText={deployment.targetSolutionName}
              className="min-w-0">
              <Label className="truncate block">
                {deployment.targetSolutionName}
              </Label>
            </WithToolTip>
          </div>
        )}

        {deployment?.targetSolutionLabel && (
          <div className="grid grid-cols-[auto_1fr_2fr] items-center gap-2 text-sm text-muted-foreground">
            <Package className="size-4 shrink-0" />
            <Label className="font-medium truncate min-w-0">
              {t("hub.catalog_subscribe.line_solution_label", {
                defaultValue: "Solution Label",
              })}
              :
            </Label>
            <WithToolTip
              tooltipText={deployment.targetSolutionLabel}
              className="min-w-0">
              <Label className="truncate block">
                {deployment.targetSolutionLabel}
              </Label>
            </WithToolTip>
          </div>
        )}

        <div className="grid grid-cols-[auto_1fr_2fr] items-center gap-2 text-sm text-muted-foreground">
          <Building className="size-4 shrink-0" />
          <Label className="font-medium truncate min-w-0">
            {t("hub.checkout.deployment_company_id", {
              defaultValue: "Company ID",
            })}
            :
          </Label>
          <Label
            className="truncate min-w-0"
            title={
              deployment?.companyId ? String(deployment.companyId) : undefined
            }>
            {deployment?.companyId}
          </Label>
        </div>

        <div className="grid grid-cols-[auto_1fr_2fr] items-center gap-2 text-sm text-muted-foreground">
          <Building2 className="size-4 shrink-0" />
          <Label className="font-medium truncate min-w-0">
            {t("hub.administer.tenant", { defaultValue: "Tenant" })}:
          </Label>
          <Label className="truncate min-w-0" title={deployment?.tenantName}>
            {deployment?.tenantName}
          </Label>
        </div>

        {solutionLabel && (
          <div className="grid grid-cols-[auto_1fr_2fr] items-center gap-2 text-sm text-muted-foreground">
            <Package className="size-4 shrink-0" />
            <Label className="font-medium truncate min-w-0">
              {t("hub.catalog_subscribe.line_solution", {
                defaultValue: "Solution",
              })}
              :
            </Label>
            <WithToolTip tooltipText={solutionLabel} className="min-w-0">
              <Label className="truncate block">{solutionLabel}</Label>
            </WithToolTip>
          </div>
        )}

        {deployment?.installedBy && (
          <div className="grid grid-cols-[auto_1fr_2fr] items-center gap-2 text-sm text-muted-foreground">
            <User className="size-4 shrink-0" />
            <Label className="font-medium truncate min-w-0">
              {t("hub.checkout.deployment_installed_by", {
                defaultValue: "Installed By",
              })}
              :
            </Label>
            <WithToolTip
              tooltipText={deployment.installedBy}
              className="min-w-0">
              <Label className="truncate block">{deployment.installedBy}</Label>
            </WithToolTip>
          </div>
        )}

        {formattedInstalledDate && (
          <div className="grid grid-cols-[auto_1fr_2fr] items-center gap-2 text-sm text-muted-foreground">
            <CalendarClock className="size-4 shrink-0" />
            <Label className="font-medium truncate min-w-0">
              {t("hub.checkout.deployment_installed_on", {
                defaultValue: "Installed On",
              })}
              :
            </Label>
            <Label className="truncate min-w-0" title={formattedInstalledDate}>
              {formattedInstalledDate}
            </Label>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
